import { getContrastColor } from '@/lib/colorUtils';
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, RefreshCw, Sparkles, Mail, Copy, Check } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { AddEventDialog } from "./AddEventDialog";
import { logger } from "@/lib/logger";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ShowingEvent {
  id: string;
  north_id: string;
  event_type: string;
  title: string;
  scheduled_date: string;
  feedback: string | null;
  links: any;
  attachments: any;
  created_at: string;
  updated_at: string;
  showing_time_start?: string | null;
  showing_time_end?: string | null;
  status?: string | null;
  source?: string | null;
  agent_name?: string | null;
  agent_company?: string | null;
  agent_email?: string | null;
  agent_phone?: string | null;
}

interface ShowingsManagerProps {
  northId: string;
  isPublished?: boolean;
  onUpdate: () => void;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  hoverColor?: string | null;
}

const ShowingsManager = ({
  northId,
  isPublished = false,
  onUpdate,
  primaryColor,
  secondaryColor,
  hoverColor,
}: ShowingsManagerProps) => {
  const [events, setEvents] = useState<ShowingEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ShowingEvent | null>(null);
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null);
  const [showOnReport, setShowOnReport] = useState(false);
  const [canSyncAirtable, setCanSyncAirtable] = useState(false);
  const [showingsSummary, setShowingsSummary] = useState("");
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const showingEmail = `north-${northId}@showings.northreports.com`;

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(showingEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const queryClient = useQueryClient();

  useEffect(() => {
    fetchEvents();
    loadVisibilitySetting();
    checkAirtableSyncAccess();
  }, [northId]);

  const checkAirtableSyncAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) return;
      
      // Only users with caldwellrg.com domain can sync from Airtable
      setCanSyncAirtable(user.email.endsWith('@caldwellrg.com'));
    } catch (error) {
      logger.error('Error checking Airtable sync access:', error);
    }
  };

  const loadVisibilitySetting = async () => {
    try {
      const { data, error } = await supabase
        .from('norths')
        .select('show_showings_on_report')
        .eq('id', northId)
        .single();

      if (error) throw error;
      if (data?.show_showings_on_report !== null && data?.show_showings_on_report !== undefined) {
        setShowOnReport(data.show_showings_on_report);
      }
    } catch (error) {
      logger.error('Error loading visibility setting:', error);
    }
  };

  const handleToggleVisibility = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from('norths')
        .update({ show_showings_on_report: checked })
        .eq('id', northId);

      if (error) throw error;
      setShowOnReport(checked);
      
      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }
      
      toast({
        title: checked ? "Shown on report" : "Hidden from report",
        description: checked 
          ? "Showings will appear on the published report" 
          : "Showings are hidden from the published report",
      });
    } catch (error) {
      logger.error('Error updating visibility:', error);
      toast({
        title: "Error",
        description: "Failed to update visibility setting",
        variant: "destructive",
      });
    }
  };

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('showing_events')
        .select('*, showing_time_start, showing_time_end')
        .eq('north_id', northId)
        .order('scheduled_date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
      
      // Invalidate the showings preview query so the report updates
      queryClient.invalidateQueries({ queryKey: ['showings-preview', northId] });
    } catch (error) {
      logger.error('Error fetching events:', error);
      toast({
        title: "Error",
        description: "Failed to load showing events",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('showing_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      toast({
        title: "Event Deleted",
        description: "Event removed successfully",
      });

      fetchEvents();
      queryClient.invalidateQueries({ queryKey: ['showings-preview', northId] });
    } catch (error) {
      logger.error('Error deleting event:', error);
      toast({
        title: "Error",
        description: "Failed to delete event",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
  };

  const handleRefreshShowings = async () => {
    try {
      setIsRefreshing(true);
      
      const { data, error } = await supabase.functions.invoke('sync-airtable', {
        body: { northId }
      });

      if (error) throw error;

      await fetchEvents();
      
      toast({
        title: "Success",
        description: data?.message || "Showings synced from Airtable",
      });
    } catch (error) {
      logger.error('Error syncing showings:', error);
      toast({
        title: "Error",
        description: "Failed to sync showings from Airtable",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatLastUpdated = (date: string | null) => {
    if (!date) return "Never";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "Never";
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  // Fetch showings summary from norths table
  const { data: northData } = useQuery({
    queryKey: ['north-showings-summary', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('norths')
        .select('showings_summary, updated_at')
        .eq('id', northId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Update local state when north data changes
  useEffect(() => {
    if (northData?.showings_summary) {
      setShowingsSummary(northData.showings_summary);
    }
  }, [northData]);

  // Generate summary mutation
  const generateSummary = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('generate-showings-summary', {
        body: { northId }
      });

      if (error) throw error;
      if (!data?.summary) throw new Error('No summary generated');

      return data.summary;
    },
    onSuccess: async (summary) => {
      setShowingsSummary(summary);

      // Save to database
      const { error } = await supabase
        .from('norths')
        .update({ showings_summary: summary })
        .eq('id', northId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['north-showings-summary', northId] });
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });

      toast({
        title: "Summary Generated",
        description: "Showings summary has been generated successfully",
      });
    },
    onError: (error: any) => {
      logger.error('Error generating summary:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate summary",
        variant: "destructive",
      });
    },
  });

  // Save summary mutation
  const saveSummary = useMutation({
    mutationFn: async (summary: string) => {
      const { error } = await supabase
        .from('norths')
        .update({ showings_summary: summary })
        .eq('id', northId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['north-showings-summary', northId] });
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });

      toast({
        title: "Summary Saved",
        description: "Showings summary has been saved successfully",
      });
    },
    onError: (error: any) => {
      logger.error('Error saving summary:', error);
      toast({
        title: "Error",
        description: "Failed to save summary",
        variant: "destructive",
      });
    },
  });

  const handleGenerateSummary = () => {
    setIsGeneratingSummary(true);
    generateSummary.mutate(undefined, {
      onSettled: () => setIsGeneratingSummary(false)
    });
  };

  const handleSaveSummary = () => {
    saveSummary.mutate(showingsSummary);
  };

  return (
    <div className="p-4 md:p-6">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="space-y-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">
            Showings & Open Houses
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Add your showings and open houses to track buyer activity and feedback
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-on-report"
              checked={showOnReport}
              onCheckedChange={handleToggleVisibility}
              disabled={isPublished}
              style={showOnReport ? { backgroundColor: '#22c55e' } : undefined}
            />
            <Label htmlFor="show-on-report" className="text-sm font-medium cursor-pointer whitespace-nowrap">
              Show on Report
            </Label>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={() => {
                setEditingEvent(null);
                setIsDialogOpen(true);
              }}
              disabled={isPublished}
              className="flex-1 sm:flex-none"
              style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
            >
              <Plus className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Add Event</span>
              <span className="md:hidden">Add</span>
            </Button>
            {canSyncAirtable && (
              <Button
                onClick={handleRefreshShowings}
                disabled={isRefreshing || isPublished}
                variant="outline"
                className="flex-1 sm:flex-none"
                style={{ borderColor: secondaryColor || '#1b489b', color: secondaryColor || '#1b489b' }}
              >
                <RefreshCw className={`h-4 w-4 md:mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Sync Showings</span>
                <span className="sm:hidden">Sync</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ShowingTime Email Integration Card */}
      <div className="border rounded-lg p-4 mb-6 bg-muted/20">
        <div className="flex items-start gap-3">
          <Mail className="h-5 w-5 mt-0.5 shrink-0" style={{ color: secondaryColor || '#1b489b' }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground mb-0.5">ShowingTime Auto-Sync</p>
            <p className="text-xs text-muted-foreground mb-3">
              In ShowingTime, add this email as a contact on your listing and set the role to <span className="font-medium text-foreground">Owner</span>. Once added, showings, confirmations, cancellations, and buyer feedback will sync here automatically.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-background border rounded px-3 py-2 font-mono truncate select-all">
                {showingEmail}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyEmail}
                className="shrink-0"
                style={{ borderColor: secondaryColor || '#1b489b', color: secondaryColor || '#1b489b' }}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Events Table */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-muted/20 rounded-lg">
          <p className="text-muted-foreground mb-2">No events yet</p>
          <p className="text-sm text-muted-foreground">Add your first showing or open house to get started</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-sm font-semibold text-foreground">Type</th>
                  <th className="text-left p-3 text-sm font-semibold text-foreground">Event</th>
                  <th className="text-left p-3 text-sm font-semibold text-foreground">Date</th>
                  <th className="text-right p-3 text-sm font-semibold text-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
              {events.map((event) => (
                <tr key={event.id} className="border-t hover:bg-muted/20">
                  <td className="p-3">
                    <div className="flex flex-col gap-1">
                      <Badge variant={event.event_type === 'showing' ? 'default' : 'secondary'}>
                        {event.event_type === 'showing' ? 'Showing' : event.event_type === 'open_house' ? 'Open House' : event.event_type}
                      </Badge>
                      {event.status && event.status !== 'confirmed' && (
                        <Badge variant="outline" className={
                          event.status === 'cancelled' ? 'border-red-400 text-red-500 text-xs' :
                          event.status === 'requested' ? 'border-amber-400 text-amber-600 text-xs' :
                          event.status === 'completed' ? 'border-blue-400 text-blue-500 text-xs' :
                          'text-xs'
                        }>
                          {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <div>
                      {event.source === 'showingtime' && (
                        <div className="flex items-center gap-1 mb-1">
                          <Mail className="h-3 w-3 text-emerald-500" />
                          <span className="text-xs text-emerald-600 font-medium">Auto Sync</span>
                        </div>
                      )}
                      <p className="font-medium text-foreground">{event.title}</p>
                      {event.agent_company && event.source === 'showingtime' && (
                        <p className="text-xs text-muted-foreground">{event.agent_company}</p>
                      )}
                      {event.feedback && (
                        <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                          {event.feedback}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {formatDate(event.showing_time_start || event.scheduled_date)}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setEditingEvent(event);
                          setIsDialogOpen(true);
                        }}
                        disabled={isPublished}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => setDeleteEventId(event.id)}
                        disabled={isPublished}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {events.map((event) => (
            <div key={event.id} className="border rounded-lg p-4 bg-card">
              <div className="flex items-start justify-between mb-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant={event.event_type === 'showing' ? 'default' : 'secondary'} className="text-xs">
                    {event.event_type === 'showing' ? 'Showing' : event.event_type === 'open_house' ? 'Open House' : event.event_type}
                  </Badge>
                  {event.status && event.status !== 'confirmed' && (
                    <Badge variant="outline" className={
                      event.status === 'cancelled' ? 'border-red-400 text-red-500 text-xs' :
                      event.status === 'requested' ? 'border-amber-400 text-amber-600 text-xs' :
                      event.status === 'completed' ? 'border-blue-400 text-blue-500 text-xs' :
                      'text-xs'
                    }>
                      {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      setEditingEvent(event);
                      setIsDialogOpen(true);
                    }}
                    disabled={isPublished}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => setDeleteEventId(event.id)}
                    disabled={isPublished}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {event.source === 'showingtime' && (
                <div className="flex items-center gap-1 mb-1">
                  <Mail className="h-3 w-3 text-emerald-500" />
                  <span className="text-xs text-emerald-600 font-medium">Auto Sync</span>
                </div>
              )}
              <h4 className="font-medium text-foreground mb-1">{event.title}</h4>
              <p className="text-sm text-muted-foreground mb-2">{formatDate(event.showing_time_start || event.showing_date || event.scheduled_date)}</p>
              {event.feedback && (
                <p className="text-sm text-muted-foreground line-clamp-2">{event.feedback}</p>
              )}
            </div>
          ))}
        </div>
      </>
      )}

      {/* AI Summary Section */}
      <div className="mt-8 border-t pt-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-1">AI Summary</h3>
          <p className="text-sm text-muted-foreground">
            Generate an AI-powered summary of your showing activity and buyer feedback
          </p>
          {northData?.updated_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Last Updated: {formatLastUpdated(northData.updated_at)}
            </p>
          )}
        </div>

        <RichTextEditor
          value={showingsSummary}
          onChange={setShowingsSummary}
          onSave={(summary) => saveSummary.mutate(summary)}
          placeholder="Click 'Generate Summary' to create an AI summary of your showings..."
          disabled={isPublished}
          className="mb-4"
        />

        <div className="flex gap-2">
          <Button
            onClick={handleGenerateSummary}
            disabled={isGeneratingSummary || isPublished || events.length === 0}
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <Sparkles className={`h-4 w-4 mr-2 ${isGeneratingSummary ? 'animate-spin' : ''}`} />
            {isGeneratingSummary ? 'Generating...' : 'Generate Summary'}
          </Button>
          <Button
            onClick={handleSaveSummary}
            variant="outline"
            disabled={!showingsSummary || isPublished || saveSummary.isPending}
            style={{ borderColor: secondaryColor || '#1b489b', color: secondaryColor || '#1b489b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = secondaryColor || '#1b489b';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = secondaryColor || '#1b489b';
            }}
          >
            {saveSummary.isPending ? 'Saving...' : 'Save Summary'}
          </Button>
        </div>

        {showingsSummary && (
          <p className="text-xs text-muted-foreground mt-2">
            This summary also appears in the AI Summaries menu on the report.
          </p>
        )}

        {events.length === 0 && (
          <p className="text-sm text-muted-foreground mt-2">
            Add showing events before generating a summary
          </p>
        )}
      </div>

      {/* Add/Edit Event Dialog */}
      <AddEventDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        northId={northId}
        event={editingEvent}
        hoverColor={hoverColor}
        onSuccess={() => {
          fetchEvents();
          setIsDialogOpen(false);
          setEditingEvent(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteEventId} onOpenChange={() => setDeleteEventId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteEventId) {
                  handleDeleteEvent(deleteEventId);
                  setDeleteEventId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ShowingsManager;